import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, LockKeyhole, ShieldAlert, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

interface Overview {
  policy: {
    maxFailures: number;
    windowMinutes: number;
    sessionHours: number;
    sourceWhitelistEnabled: boolean;
    turnstileEnabled: boolean;
  };
  stats: { failed24h: number; success24h: number; activeSessions: number };
  recentAttempts: { id: string; ip: string | null; success: boolean; createdAt: string }[];
}

interface SecurityConfig {
  sourceWhitelistEnabled: boolean;
  allowedIps: string[];
  turnstileEnabled: boolean;
  turnstileSiteKey: string | null;
  hasTurnstileSecret: boolean;
}

interface SessionRow {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

export function SettingsSecurityPage() {
  const queryClient = useQueryClient();
  const overview = useQuery({
    queryKey: ["security-overview"],
    queryFn: () => apiFetch<Overview>("/api/v1/admin/security/overview"),
  });
  const sessions = useQuery({
    queryKey: ["auth-sessions"],
    queryFn: () => apiFetch<SessionRow[]>("/api/v1/auth/sessions"),
  });
  const configQuery = useQuery({
    queryKey: ["security-config"],
    queryFn: () => apiFetch<SecurityConfig>("/api/v1/admin/settings/security"),
  });
  const [sourceEnabled, setSourceEnabled] = useState(false);
  const [allowedIps, setAllowedIps] = useState("");
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [siteKey, setSiteKey] = useState("");
  const [turnstileSecret, setTurnstileSecret] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const data = configQuery.data;
    if (!data) return;
    setSourceEnabled(data.sourceWhitelistEnabled);
    setAllowedIps(data.allowedIps.join("\n"));
    setTurnstileEnabled(data.turnstileEnabled);
    setSiteKey(data.turnstileSiteKey ?? "");
    setTurnstileSecret("");
  }, [configQuery.data]);

  async function saveSecurity() {
    setSaving(true);
    try {
      await apiFetch("/api/v1/admin/settings/security", {
        method: "PUT",
        body: JSON.stringify({
          sourceWhitelistEnabled: sourceEnabled,
          allowedIps: allowedIps
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter(Boolean),
          turnstileEnabled,
          turnstileSiteKey: siteKey.trim() || null,
          turnstileSecret: turnstileSecret.trim() || null,
        }),
      });
      await Promise.all([
        configQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ["security-overview"] }),
      ]);
      toast.success("安全设置已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function revokeOthers() {
    try {
      await apiFetch("/api/v1/auth/sessions/revoke-others", { method: "POST" });
      await queryClient.invalidateQueries({ queryKey: ["auth-sessions"] });
      toast.success("已撤销其他会话");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "撤销失败");
    }
  }

  const stats = overview.data?.stats;

  return (
    <div className="space-y-6">
      <PageHeader
        title="安全与登录"
        description="登录限速、锁定窗口、会话有效期与当前会话管理。"
        actions={
          <Button variant="outline" onClick={revokeOthers}>
            <ShieldAlert className="h-4 w-4" />
            撤销其他会话
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>登录来源与 Turnstile</CardTitle>
          <CardDescription>
            来源白名单按 CF-Connecting-IP 精确匹配；Turnstile 在登录页显示人机验证。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={sourceEnabled}
              onChange={(event) => setSourceEnabled(event.target.checked)}
            />
            <span>
              启用来源白名单
              <span className="block text-xs text-muted-foreground">
                开启后仅允许下列 IP 登录；留空会阻止所有登录。
              </span>
            </span>
          </label>
          <div className="space-y-2">
            <Label>允许的 IP（每行一个）</Label>
            <textarea
              className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-sm"
              value={allowedIps}
              onChange={(event) => setAllowedIps(event.target.value)}
              placeholder="203.0.113.10"
            />
          </div>
          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={turnstileEnabled}
              onChange={(event) => setTurnstileEnabled(event.target.checked)}
            />
            <span>
              启用 Cloudflare Turnstile
              <span className="block text-xs text-muted-foreground">
                需要 Site Key 与 Secret；登录页会加载 Turnstile。
              </span>
            </span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Turnstile Site Key</Label>
              <Input value={siteKey} onChange={(event) => setSiteKey(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                Turnstile Secret{" "}
                {configQuery.data?.hasTurnstileSecret ? "（已保存，留空不修改）" : ""}
              </Label>
              <Input
                type="password"
                value={turnstileSecret}
                onChange={(event) => setTurnstileSecret(event.target.value)}
              />
            </div>
          </div>
          <Button onClick={saveSecurity} disabled={saving}>
            {saving ? "保存中…" : "保存安全设置"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="活跃会话" value={stats?.activeSessions ?? 0} icon={Laptop} />
        <StatCard
          label="24h 登录成功"
          value={stats?.success24h ?? 0}
          icon={Users}
          accent="success"
        />
        <StatCard
          label="24h 登录失败"
          value={stats?.failed24h ?? 0}
          icon={ShieldAlert}
          accent="danger"
        />
        <StatCard
          label="登录锁定阈值"
          value={
            overview.data
              ? `${overview.data.policy.maxFailures} 次 / ${overview.data.policy.windowMinutes} 分钟`
              : "—"
          }
          icon={LockKeyhole}
          accent="warning"
        />
      </div>

      <Alert variant="info" title="当前生效策略">
        {overview.data
          ? `会话 ${overview.data.policy.sessionHours} 小时；同一账号 ${overview.data.policy.windowMinutes} 分钟内失败 ${overview.data.policy.maxFailures} 次锁定。来源白名单与 Turnstile 未启用。`
          : "加载中…"}
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>我的会话</CardTitle>
          <CardDescription>当前浏览器会话标记为“当前”；撤销后其他设备需重新登录。</CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP</TableHead>
                  <TableHead>设备</TableHead>
                  <TableHead>最近活动</TableHead>
                  <TableHead>过期时间</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sessions.data ?? []).map((session) => (
                  <TableRow key={session.id}>
                    <TableCell className="font-mono text-xs">{session.ip ?? "—"}</TableCell>
                    <TableCell className="max-w-80 truncate text-xs">
                      {session.userAgent ?? "—"}
                    </TableCell>
                    <TableCell>{formatDateTime(session.lastSeenAt)}</TableCell>
                    <TableCell>{formatDateTime(session.expiresAt)}</TableCell>
                    <TableCell>
                      <Badge variant={session.current ? "success" : "secondary"}>
                        {session.current ? "当前" : "其他设备"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>最近登录尝试</CardTitle>
          <CardDescription>最近 20 条；不展示登录标识，仅展示结果与来源 IP。</CardDescription>
        </CardHeader>
        <CardContent>
          {(overview.data?.recentAttempts ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无登录记录。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>时间</TableHead>
                  <TableHead>结果</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(overview.data?.recentAttempts ?? []).map((attempt) => (
                  <TableRow key={attempt.id}>
                    <TableCell>{formatDateTime(attempt.createdAt)}</TableCell>
                    <TableCell>
                      <Badge variant={attempt.success ? "success" : "danger"}>
                        {attempt.success ? "成功" : "失败"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{attempt.ip ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
